import React, { useEffect, useMemo, useState } from 'react';
import {
  App,
  Button,
  Col, Divider, Form, FormItemProps, Input, InputNumber, Row, Select, Switch, Tooltip,
} from 'antd';
import { ExclamationCircleFilled, SendOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { FieldData } from 'rc-field-form/es/interface';
import {
  capitalizeFirstLetter,
  enumToOptionsArray,
  filterObjectProps,
  normalizeArrayParams,
  queryFilterParams,
} from '../../../../utils';
import { UserRoleDisplay, UserStatusDisplay } from '../../../../enums/user';
import {
  emailStatusOptions,
  InviteUsersSubmitProps,
  SendEmailEnum,
  sendEmailOptions,
} from '../../../../hooks/api/inviteUsers';
import { useInviteUsersContext } from '../../../../context/inviteUsersContext';
import { Option } from '../../../../types';
import { useSimpleModal } from '../../../Common/Modal/Simple';
import { useTariffsTableGet } from '../../../../hooks/api/tariffs';
import TariffsSelect from '../../../Common/TariffsSelect';

const stringsArrToOptions = <T extends readonly (string | number)[]>(
  arr: T,
): Option[] => arr.map((item) => ({
    label: typeof item === 'number' ? item : capitalizeFirstLetter(item),
    value: item,
  }));

interface ModifiedFormItemProps extends FormItemProps {
  element: React.ReactNode;
  colSpan?: number;
  isHidden?: boolean;
}

function ColItem({
  element, colSpan, name, rules, isHidden, ...formItemProps
}: ModifiedFormItemProps) {
  return (
    <Col key={name} span={colSpan || 12} style={isHidden ? { display: 'none' } : undefined}>
      <Form.Item name={name} {...formItemProps} rules={[...(rules || [])]}>
        {/* TODO add? rules={[...(rules || []), validationRules]} */}
        {element}
      </Form.Item>
    </Col>
  );
}

const formItems: ModifiedFormItemProps[] = [
  {
    name: ['email'],
    label: 'Email',
    element: <Input placeholder="Enter" />,
  },
  {
    name: 'roles',
    label: 'Roles',
    element: <Select mode="multiple" options={enumToOptionsArray(UserRoleDisplay)} placeholder="Select" allowClear />,
  },
  {
    name: 'tariffId',
    label: 'Tariff ID',
    element: <InputNumber placeholder="Enter" style={{ width: '100%' }} />,
  },
  {
    name: 'tariffNames',
    label: 'Tariff Names',
    element: <TariffsSelect />,
  },
  {
    name: 'emailStatus',
    label: 'Emails status',
    element: <Select
      options={stringsArrToOptions(emailStatusOptions)}
      placeholder="Select"
      allowClear
    />,
    tooltip:
      'Filters users by the status of received emails. ',
    // + 'Has effect only if "Email type" is also selected',
  },
  {
    // isHidden: true,
    name: 'emailType',
    label: 'Email type',
    element: <Select
      options={stringsArrToOptions(sendEmailOptions)}
      placeholder="Select"
      allowClear
    />,
    tooltip:
      'Filters users by type of email they received. '
      + 'Has effect only if "Email status" is also selected',
  },
  {
    name: 'status',
    label: 'Status',
    element: <Select
      options={enumToOptionsArray(UserStatusDisplay)}
      placeholder="Select"
      allowClear
    />,
    tooltip: (
      <div>
        Filter users by status:
        <ul style={{ paddingInlineStart: '20px' }}>
          <li>
            <b>Active</b>
            : Users who have activated their account and set a password.
          </li>
          <li>
            <b>Pending</b>
            : Users who have not yet activated their account.
          </li>
          <li>
            <b>Deactivated/Archived</b>
            : Users who are no longer active.
          </li>
        </ul>
      </div>
    ),
  },
  {
    name: 'isContractPresent',
    label: 'Only for users with contracts',
    element: <Switch checkedChildren="Yes" unCheckedChildren="No" />,
  },
];

/** Form items which send only with form */
const submitFormItems: ModifiedFormItemProps[] = [
  {
    name: 'limit',
    label: 'Limit',
    element: <InputNumber min={1} max={100000} placeholder="Enter" style={{ width: '100%' }} step={10} />,
    tooltip: 'Maximum number of users to whom an email can be sent',
  },
  {
    name: 'sendEmailType',
    label: 'Sending email type',
    element: <Select options={stringsArrToOptions(sendEmailOptions)} placeholder="Select" />,
    tooltip: 'Type of email, which you want to send',
  },
];

const initialState: InviteUsersSubmitProps = {
  sendEmailType: SendEmailEnum.CREATE_ACCOUNT,
  limit: 1000, // 5000,
  tariffId: undefined,
  emailType: SendEmailEnum.CREATE_ACCOUNT,
  emailStatus: 'not invited',
  isContractPresent: true,
};

function FormInviteUsers() {
  const { message } = App.useApp();
  const { open, contextHolder } = useSimpleModal();
  const [form] = Form.useForm<InviteUsersSubmitProps>();
  const {
    usersInviteTableGet, fetchUsersInviteTable, isSubmitActive, handleIsSubmitActive, inviteUsersBulk,
  } = useInviteUsersContext();
  const tariffsGet = useTariffsTableGet();

  const tariffOptions: Option[] = useMemo(() => {
    if (!tariffsGet.data?.data) return [];

    return tariffsGet.data.data.map((item) => ({
      label: item.name,
      value: item.name,
    }));
  }, [tariffsGet.data?.data]);

  /** Initial values \/ */
  const [searchParams] = useSearchParams();
  const [initialValues, setInitialValues] = useState(initialState);

  useEffect(() => {
    const newParams = { ...(Object.fromEntries(searchParams.entries()) || {}) };

    if (newParams) {
      setInitialValues({
        ...newParams,
        tariffNames: newParams.tariffNames ? newParams?.tariffNames?.split(',') || [] : [],
        roles: newParams.roles ? newParams?.roles?.split(',') || [] : [],
        emailType: newParams.emailType || SendEmailEnum.CREATE_ACCOUNT,
        emailStatus: newParams.emailStatus || 'not invited',
        isContractPresent: newParams.isContractPresent !== 'false',
      } as any);
    }
  }, []);

  useEffect(() => {
    if (initialValues) {
      form.resetFields();
      form.setFieldsValue(initialValues);
      handleFilter(); // Trigger to add emailType and emailStatus in query after initialValues change
    }
  }, [initialValues]);

  /** Filters */
  const handleFilter = () => {
    form.validateFields().then(async (values) => {
      fetchUsersInviteTable(values);
    });
  };

  const handleClear = async () => {
    form.resetFields();
    fetchUsersInviteTable({}, true);
  };

  /** Submit */
  const handleSubmit = async () => {
    // await handleFilter();
    // await asyncDelay(100);

    form.validateFields().then((values) => {
      const filtered = queryFilterParams(
        filterObjectProps({ ...values }, ['limit', 'sendEmailType']), // as Record<string, unknown>
      );
      // const queryParams = toBracketedQueryParams(normalizeArrayParams(filtered, ['roles', 'tariffNames']));
      const normalizedParams = normalizeArrayParams(filtered, ['roles', 'tariffNames']);

      const payload: InviteUsersSubmitProps = {
        limit: values.limit,
        sendEmailType: values.sendEmailType,
        ...normalizedParams,
      };

      open({
        icon: <ExclamationCircleFilled />,
        title: 'Send emails?',
        content: (
          <span>
            Are you sure you want to send
            {/* {' '}
            <b>{val.limit}</b> */}
            {' '}
            <b>{values.sendEmailType}</b>
            {' '}
            emails?
          </span>
        ),
        cancelText: 'Cancel',
        okText: 'Send emails',
        onOk: () => inviteUsersBulk.fetch(payload) // , `?${queryParams}`)
          .then((res) => {
            if (res?.success) {
              message.success('Invitations sending started!');
            }
          }),
      });
    });
  };

  const handleFieldsChange = (changedFields: FieldData[]) => {
    const ignoredFields = new Set(['limit', 'sendEmailType']);
    const shouldIgnore = changedFields.some((field) => ignoredFields.has(field.name?.[0]));

    if (!shouldIgnore) {
      handleIsSubmitActive(false);
    }
  };

  return (
    <Form
      form={form}
      name="inviteUsers"
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={initialState}
      onFieldsChange={(changedFields) => handleFieldsChange(changedFields)}
    >
      <Divider>Filters</Divider>
      {contextHolder}
      <Row gutter={16}>
        {formItems.map((props) => <ColItem key={props.name} {...props} />)}
        <div className="flex-align-end">
          <Button onClick={handleClear}>Clear</Button>
          <Button type="primary" onClick={handleFilter} loading={usersInviteTableGet.loading}>Filter</Button>
        </div>

        <Divider>Emails settings</Divider>
        {submitFormItems.map((props) => <ColItem key={props.name} {...props} />)}

        <div className="flex-align-end">
          <Tooltip title={!isSubmitActive
            ? 'Please "filter" the list to apply your changes before sending emails.' : undefined}
          >
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={form.submit}
              disabled={!isSubmitActive}
              loading={inviteUsersBulk.loading}
            >
              Send Emails
            </Button>
          </Tooltip>
        </div>
      </Row>
    </Form>
  );
}

export default FormInviteUsers;
